import { useCallback, useState, useEffect } from 'react'
import { Upload, X, FileText, Image as ImageIcon, FileSpreadsheet, File } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  EXTENSIONES_PERMITIDAS,
  LIMITE_ARCHIVO_MB,
  esArchivoPermitido,
  esTamanoPermitido,
  formatFileSize,
} from '@/types/documentos'

interface DropZoneProps {
  onFilesSelected: (files: File[]) => void
  multiple?: boolean
  disabled?: boolean
  className?: string
  maxFiles?: number
}

function getFileIcon(file: File) {
  const type = file.type
  if (type.startsWith('image/')) return <ImageIcon className="h-8 w-8 text-blue-500" />
  if (type === 'application/pdf') return <FileText className="h-8 w-8 text-red-500" />
  if (type.includes('spreadsheet') || type.includes('excel'))
    return <FileSpreadsheet className="h-8 w-8 text-green-500" />
  return <File className="h-8 w-8 text-gray-500" />
}

export function DropZone({
  onFilesSelected,
  multiple = false,
  disabled = false,
  className,
  maxFiles = 10,
}: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [imagePreviews, setImagePreviews] = useState<Map<string, string>>(new Map())

  // Generar previews para imágenes
  useEffect(() => {
    const newPreviews = new Map<string, string>()
    const objectUrls: string[] = []

    for (const file of selectedFiles) {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file)
        newPreviews.set(`${file.name}-${file.size}`, url)
        objectUrls.push(url)
      }
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- setImagePreviews es consecuencia de crear ObjectURLs que requieren cleanup en el return del effect; useEffectEvent no soporta cleanup
    setImagePreviews(newPreviews)

    // Cleanup: revocar URLs al desmontar o cuando cambian los archivos
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [selectedFiles])

  const validateFiles = useCallback(
    (files: File[]): { valid: File[]; errors: string[] } => {
      const valid: File[] = []
      const errorMessages: string[] = []

      for (const file of files) {
        if (!esArchivoPermitido(file)) {
          errorMessages.push(`"${file.name}" no es un tipo de archivo permitido`)
          continue
        }
        if (!esTamanoPermitido(file)) {
          errorMessages.push(`"${file.name}" excede el límite de ${LIMITE_ARCHIVO_MB}MB`)
          continue
        }
        valid.push(file)
      }

      // Limitar cantidad de archivos
      if (!multiple && valid.length > 1) {
        return { valid: [valid[0]], errors: errorMessages }
      }

      if (valid.length > maxFiles) {
        errorMessages.push(`Solo se permiten ${maxFiles} archivos como máximo`)
        return { valid: valid.slice(0, maxFiles), errors: errorMessages }
      }

      return { valid, errors: errorMessages }
    },
    [multiple, maxFiles]
  )

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || disabled) return

      const fileArray = Array.from(files)
      const { valid, errors: validationErrors } = validateFiles(fileArray)

      setErrors(validationErrors)
      setSelectedFiles(valid)

      if (valid.length > 0) {
        onFilesSelected(valid)
      }
    },
    [disabled, validateFiles, onFilesSelected]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragging(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      handleFiles(e.dataTransfer.files)
    },
    [handleFiles]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files)
      // Reset input para permitir seleccionar el mismo archivo
      e.target.value = ''
    },
    [handleFiles]
  )

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = selectedFiles.filter((_, i) => i !== index)
      setSelectedFiles(newFiles)
      onFilesSelected(newFiles)
    },
    [selectedFiles, onFilesSelected]
  )

  const clearAll = useCallback(() => {
    setSelectedFiles([])
    setErrors([])
    onFilesSelected([])
  }, [onFilesSelected])

  return (
    <div className={cn('space-y-4', className)}>
      {/* Área de drop */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'cursor-pointer'
        )}
      >
        <input
          type="file"
          multiple={multiple}
          onChange={handleInputChange}
          disabled={disabled}
          accept={EXTENSIONES_PERMITIDAS.join(',')}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <div className="flex flex-col items-center gap-2">
          <Upload
            className={cn(
              'h-10 w-10 transition-colors',
              isDragging ? 'text-primary' : 'text-muted-foreground'
            )}
          />
          <div className="text-sm">
            <span className="font-medium text-primary">Haz clic para seleccionar</span>
            <span className="text-muted-foreground"> o arrastra archivos aquí</span>
          </div>
          <p className="text-xs text-muted-foreground">
            PDF, imágenes, Word, Excel. Máx. {LIMITE_ARCHIVO_MB}MB por archivo.
          </p>
        </div>
      </div>

      {/* Errores de validación */}
      {errors.length > 0 && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
          <ul className="list-disc list-inside space-y-1">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Lista de archivos seleccionados */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedFiles.length} archivo{selectedFiles.length > 1 ? 's' : ''} seleccionado
              {selectedFiles.length > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Limpiar
            </button>
          </div>

          <div className="space-y-2">
            {selectedFiles.map((file, index) => {
              const previewKey = `${file.name}-${file.size}`
              const previewUrl = imagePreviews.get(previewKey)
              const isImage = file.type.startsWith('image/')

              return (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                >
                  {/* Preview de imagen o icono */}
                  {isImage && previewUrl ? (
                    <div className="relative h-12 w-12 rounded overflow-hidden bg-muted shrink-0">
                      <img
                        src={previewUrl}
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    getFileIcon(file)
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-muted rounded"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
